import { useLayoutEffect, useRef, useState } from 'react'
import { chatError } from '@/lib/support-chat/client'
import type { ChatConversation, ChatRead } from '@/lib/support-chat/types'
import { ChatMessages } from './ChatMessages'
import { mergeMessages, receiveLatestPage } from '@/lib/support-chat/history'
import { useConversationRequest } from './useConversationRequest'

export function ConversationHistory({
  conversation,
  browserContext,
}: {
  conversation: ChatConversation
  browserContext: string
}) {
  const [latestPage, setLatestPage] = useState(conversation)
  const [history, setHistory] = useState(() => ({
    messages: conversation.messages,
    cursor: conversation.olderCursor,
  }))
  if (latestPage !== conversation) {
    setLatestPage(conversation)
    setHistory(previous => receiveLatestPage(previous, conversation))
  }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { messages, cursor } = history
  const request = useConversationRequest(browserContext)
  const viewport = useRef<HTMLDivElement>(null)
  const followLatest = useRef(true)
  const previousPosition = useRef<{ height: number; top: number } | null>(null)
  const otherMessageId =
    [...messages].reverse().find(message => message.authorName !== 'You')?.id ??
    null
  // A poll can deliver the new message while teamTyping is still true. Hide the
  // indicator for that update, then show it again on the next conversation.
  const [typingGate, setTypingGate] = useState({
    conversation,
    otherMessageId,
    messageJustArrived: false,
  })
  if (
    typingGate.conversation !== conversation ||
    typingGate.otherMessageId !== otherMessageId
  ) {
    setTypingGate({
      conversation,
      otherMessageId,
      messageJustArrived: typingGate.otherMessageId !== otherMessageId,
    })
  }
  const showTeamTyping = conversation.teamTyping && !typingGate.messageJustArrived

  useLayoutEffect(() => {
    const element = viewport.current
    if (!element) return
    if (previousPosition.current) {
      const previous = previousPosition.current
      element.scrollTop = previous.top + element.scrollHeight - previous.height
      previousPosition.current = null
    } else if (followLatest.current) {
      element.scrollTop = element.scrollHeight
    }
  }, [messages, conversation.teamTyping])

  async function readPage(before: string) {
    const result = await request<ChatRead>({
      query: new URLSearchParams({ before }),
    })
    if (
      result.browserContext !== browserContext ||
      result.data.conversation?.ticketId !== conversation.ticketId
    ) {
      throw new Error(
        'The conversation changed. Open it again before continuing.'
      )
    }
    return result.data.conversation
  }

  async function loadOlder() {
    if (!cursor) return
    setBusy(true)
    setError(null)
    try {
      const page = await readPage(cursor)
      if (viewport.current) {
        previousPosition.current = {
          height: viewport.current.scrollHeight,
          top: viewport.current.scrollTop,
        }
        followLatest.current = false
      }
      setHistory(previous => ({
        messages: mergeMessages(page.messages, previous.messages),
        // A newer poll can uncover another gap while this page is loading.
        cursor: previous.cursor === cursor ? page.olderCursor : previous.cursor,
      }))
    } catch (error) {
      setError(chatError(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-2">
      {cursor && (
        <button
          type="button"
          onClick={() => void loadOlder()}
          disabled={busy}
          className="text-xs text-gray-600 underline"
        >
          {busy ? 'Loading…' : 'Earlier messages'}
        </button>
      )}
      <div
        ref={viewport}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
        role="region"
        aria-label="Conversation history"
        tabIndex={0}
        onScroll={event => {
          const element = event.currentTarget
          followLatest.current =
            element.scrollHeight - element.scrollTop - element.clientHeight < 48
        }}
      >
        <ChatMessages messages={messages} browserContext={browserContext} />
        {showTeamTyping && (
          <p role="status" className="mt-3 flex items-end gap-2">
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-vi-red text-[10px] font-semibold text-white"
              aria-hidden="true"
            >
              VI
            </span>
            <span className="rounded-2xl rounded-bl-md bg-gray-100 px-3.5 py-2 text-sm text-gray-500">
              Typing…
            </span>
          </p>
        )}
      </div>
      {error && (
        <p role="alert" className="text-xs text-red-700">
          {error}
        </p>
      )}
    </section>
  )
}
